// ON-407: what an auth round trip left on the event page URL, and what the
// page should do about it.
//
// A web signup's confirmation email can come back two ways, depending on the
// Supabase email template in use:
//  (a) custom template → {SITE}/auth/confirm?token_hash=..&type=signup&redirect_to=..
//      AuthConfirm verifies the OTP, drops a one-shot marker (markEmailConfirmed)
//      and navigates to redirect_to (the event page).
//  (b) default {{ .ConfirmationURL }} → GoTrue /verify confirms, then 302s to
//      emailRedirectTo (the event page) with `#access_token=..&type=signup`
//      (implicit flow, this client's default) or `?code=` (PKCE). supabase-js
//      (detectSessionInUrl) turns that into a session and strips the URL, so
//      the URL is snapshotted at load, before the client touches it.
// Google / Apple OAuth return the same way as (b), minus `type`; a
// sessionStorage marker set before the redirect tells the two apart.

export type AuthReturn =
  | { kind: 'none' }
  /** Implicit-flow tokens in the fragment; `type` is 'signup' for an email confirmation. */
  | { kind: 'session'; type: string | null }
  /** PKCE `?code=` — exchanged by supabase-js if this browser holds the verifier. */
  | { kind: 'code' }
  | { kind: 'error'; code: string | null }

export function classifyAuthReturn(search: string, hash: string): AuthReturn {
  const q = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash)
  const error = h.get('error') ?? q.get('error')
  if (error) return { kind: 'error', code: h.get('error_code') ?? q.get('error_code') ?? error }
  if (h.get('access_token')) return { kind: 'session', type: h.get('type') }
  if (q.get('code')) return { kind: 'code' }
  return { kind: 'none' }
}

// Snapshot of the URL this page was loaded with. Imported by supabase.ts
// before the client is created, so it predates detectSessionInUrl's cleanup.
const initial =
  typeof window === 'undefined'
    ? { pathname: '', authReturn: { kind: 'none' } as AuthReturn }
    : {
        pathname: window.location.pathname,
        authReturn: classifyAuthReturn(window.location.search, window.location.hash),
      }
let initialConsumed = false

/** The load-time auth return, once, and only for the page it was loaded on. */
export function takeInitialAuthReturn(pathname: string): AuthReturn {
  if (initialConsumed || pathname !== initial.pathname) return { kind: 'none' }
  initialConsumed = true
  return initial.authReturn
}

export type PendingAuth = 'google' | 'apple'

export type MemoryStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const PENDING_KEY = 'stazon:pending-auth'
const CONFIRMED_KEY = 'stazon:email-confirmed'

function session(): MemoryStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function setPendingAuth(method: PendingAuth, store: MemoryStorage | null = session()): void {
  try {
    store?.setItem(PENDING_KEY, method)
  } catch {
    /* storage blocked */
  }
}

export function takePendingAuth(store: MemoryStorage | null = session()): PendingAuth | null {
  try {
    const v = store?.getItem(PENDING_KEY) ?? null
    store?.removeItem(PENDING_KEY)
    return v === 'google' || v === 'apple' ? v : null
  } catch {
    return null
  }
}

/** Set by AuthConfirm (path a) right before following redirect_to. */
export function markEmailConfirmed(store: MemoryStorage | null = session()): void {
  try {
    store?.setItem(CONFIRMED_KEY, '1')
  } catch {
    /* storage blocked */
  }
}

export function takeEmailConfirmed(store: MemoryStorage | null = session()): boolean {
  try {
    const v = store?.getItem(CONFIRMED_KEY) === '1'
    store?.removeItem(CONFIRMED_KEY)
    return v
  } catch {
    return false
  }
}

export type ReturnStep =
  /** Signed in and back with rsvp=confirm: show the confirm-RSVP prompt. */
  | 'confirm'
  /** Email confirmed but no session here (PKCE code from another browser). */
  | 'login_after_confirm'
  /** The confirmation link expired / was already used. */
  | 'login_link_expired'
  /** Some other auth failure: plain login sheet. */
  | 'login'

export interface ResolvedReturn {
  step: ReturnStep | null
  /** A web signup's email was just confirmed (fires registration_completed). */
  emailConfirmed: boolean
  /** How this auth round trip happened, for auth_completed. */
  method: 'email' | PendingAuth | null
  /** The confirmation link landed directly on this page (path b) — fires auth_link_opened here. */
  linkOpenedHere: boolean
}

export function resolveAuthReturn(input: {
  authReturn: AuthReturn
  hasSession: boolean
  rsvpConfirm: boolean
  pending: PendingAuth | null
  confirmedViaAuthConfirm: boolean
}): ResolvedReturn {
  const { authReturn, hasSession, rsvpConfirm, pending, confirmedViaAuthConfirm } = input

  const viaUrl =
    !pending && ((authReturn.kind === 'session' && authReturn.type === 'signup') || authReturn.kind === 'code')
  const oauthReturn = !!pending && (authReturn.kind === 'session' || authReturn.kind === 'code')
  const emailConfirmed = confirmedViaAuthConfirm || viaUrl
  const method = emailConfirmed ? 'email' : oauthReturn ? pending : null

  if (hasSession) {
    return { step: rsvpConfirm ? 'confirm' : null, emailConfirmed, method, linkOpenedHere: viaUrl }
  }
  if (authReturn.kind === 'code' && !pending) {
    return { step: 'login_after_confirm', emailConfirmed: true, method: 'email', linkOpenedHere: true }
  }
  if (authReturn.kind === 'error') {
    const expired = authReturn.code === 'otp_expired'
    return { step: expired ? 'login_link_expired' : 'login', emailConfirmed: false, method: null, linkOpenedHere: false }
  }
  if (authReturn.kind !== 'none') {
    return { step: 'login', emailConfirmed: false, method: null, linkOpenedHere: false }
  }
  return { step: null, emailConfirmed: false, method: null, linkOpenedHere: false }
}

const FRESH_MS = 10 * 60 * 1000

/**
 * Whether an account was created moments ago — how an OAuth return tells a
 * brand-new account ('new') from a returning one ('existing'); OAuth gives
 * the client no explicit signal.
 */
export function isFreshAccount(createdAt: string | undefined | null, now: Date = new Date()): boolean {
  if (!createdAt) return false
  const at = Date.parse(createdAt)
  if (Number.isNaN(at)) return false
  return now.getTime() - at < FRESH_MS
}
